import type { AnnotationLabelT } from '../../../../common/types/types';
import { interpolateCuboid } from '../../components/annotations/cuboid/utils';
import { interpolateEllipse } from '../../components/annotations/ellipse/utils';
import { interpolateKeyPoint } from '../../components/annotations/key-point/utils';
import { interpolatePolyline } from '../../components/annotations/polyline/utils';
import { interpolateRectangle } from '../../components/annotations/rectangle/utils';
import { interpolateSkeleton } from '../../components/annotations/skeleton/util';
import { ANNOTATION_TYPE } from '../../constants';
import type { AnnotationDataT, KeyFrameT } from '../../types';
type BaseAnnotationStore<DataT, AnnotationTypesT> = {
  annotationStore: {
    [id: string]: {
      data: DataT;
      annotationType: AnnotationTypesT;
      annotationLabel: AnnotationLabelT;
      keyFrame?: KeyFrameT;
    };
  };
};
export const interpolateKeyFrames = <AnnotationTypesT, DataT, FrameStateT extends BaseAnnotationStore<DataT, AnnotationTypesT>>(frames: FrameStateT[], modifiedKeyFrame: number, annotationIdx: string, interpolateShape: (annotationType: AnnotationTypesT, fromData: DataT, toData: DataT, interpolationFactor: number) => DataT): FrameStateT[] => {
  // Find keyFrame before the current frame
  let prevKeyFrame = -1;
  for (let i = modifiedKeyFrame - 1; i >= 0; i--) {
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    const annotation = frames[i].annotationStore[annotationIdx];
    if (!annotation) {
      // Annotation doesn't exist in previous frame
      break;
    }
    if (annotation.keyFrame?.isKeyFrame) {
      prevKeyFrame = i;
      break;
    }
  }
  if (prevKeyFrame !== -1) {
    frames = interpolateAnnotations(frames, annotationIdx, prevKeyFrame, modifiedKeyFrame, interpolateShape);
  }

  // Find keyFrame after the current frame
  let nextKeyFrame = -1;
  for (let i = modifiedKeyFrame + 1; i < frames.length; i++) {
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    const annotation = frames[i].annotationStore[annotationIdx];
    if (!annotation) {
      // Annotation doesn't exist in previous frame
      break;
    }
    if (annotation.keyFrame?.isKeyFrame) {
      nextKeyFrame = i;
      break;
    }
  }
  if (nextKeyFrame !== -1) {
    frames = interpolateAnnotations<AnnotationTypesT, DataT, FrameStateT>(frames, annotationIdx, modifiedKeyFrame, nextKeyFrame, interpolateShape);
    return frames;
  }
  // Moving interpolated frames to the last key frame's position
  for (let i = modifiedKeyFrame + 1; i < frames.length; i++) {
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    if (!frames[i].annotationStore[annotationIdx]) {
      break;
    }
    // @ts-expect-error todo(ts-migration) TS2322 Type '{ annotationStore: { [x: string]: { data: DataT; annotationType: AnnotationTypesT; annotationLabel: AnnotationLabelT; keyFrame?: KeyFrameT; } | { data: DataT; annotationType?: AnnotationTypesT |...
    frames[i] = {
      ...frames[i],
      annotationStore: {
        // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
        ...frames[i].annotationStore,
        [annotationIdx]: {
          // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
          ...frames[i].annotationStore[annotationIdx],
          data: {
            // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
            ...frames[modifiedKeyFrame].annotationStore[annotationIdx].data
          }
        }
      }
    };
  }
  return frames;
};
export function interpolateAnnotations<AnnotationTypesT, DataT, FrameStateT extends BaseAnnotationStore<DataT, AnnotationTypesT>>(frames: FrameStateT[], annotationIdx: string, startKeyFrame: number, endKeyFrame: number, interpolateShape: (annotationType: AnnotationTypesT, fromData: DataT, toData: DataT, interpolationFactor: number) => DataT): FrameStateT[] {
  // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
  const fromAnnotation = frames[startKeyFrame].annotationStore[annotationIdx];
  // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
  const toAnnotation = frames[endKeyFrame].annotationStore[annotationIdx];
  const deltaFrames = endKeyFrame - startKeyFrame;
  for (let i = startKeyFrame + 1; i < endKeyFrame; i++) {
    // @ts-expect-error todo(ts-migration) TS2322 Type '{ annotationStore: { [x: string]: { data: DataT; annotationType: AnnotationTypesT; annotationLabel: AnnotationLabelT; keyFrame?: KeyFrameT; } | { data: DataT; annotationType?: AnnotationTypesT |...
    frames[i] = {
      ...frames[i],
      annotationStore: {
        // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
        ...frames[i].annotationStore,
        [annotationIdx]: {
          // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
          ...frames[i].annotationStore[annotationIdx],
          data: {
            // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
            ...frames[i].annotationStore[annotationIdx].data
          }
        }
      }
    };

    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    const frameAnnotation = frames[i].annotationStore[annotationIdx];
    const interpolationFactor = (i - startKeyFrame) / deltaFrames;
    let interpolatedShape = interpolateShape(
    // @ts-expect-error todo(ts-migration) TS18048 'frameAnnotation' is possibly 'undefined'.
    frameAnnotation.annotationType,
    // @ts-expect-error todo(ts-migration) TS18048 'fromAnnotation' is possibly 'undefined'.
    fromAnnotation.data,
    // @ts-expect-error todo(ts-migration) TS18048 'toAnnotation' is possibly 'undefined'.
    toAnnotation.data, interpolationFactor);
    // @ts-expect-error todo(ts-migration) TS18048 'frameAnnotation' is possibly 'undefined'.
    frameAnnotation.data = interpolatedShape;
  }
  return frames;
}
export function interpolateShapes(annotationType: (typeof ANNOTATION_TYPE)[keyof typeof ANNOTATION_TYPE], fromData: AnnotationDataT, toData: AnnotationDataT, interpolationFactor: number) {
  switch (annotationType) {
    case ANNOTATION_TYPE.RECTANGLE:
      // @ts-ignore The case check confirms that from and toData will be correct type.
      return interpolateRectangle(fromData, toData, interpolationFactor);
    case ANNOTATION_TYPE.KEY_POINT:
      // @ts-ignore The case check confirms that from and toData will be correct type.
      return interpolateKeyPoint(fromData, toData, interpolationFactor);
    case ANNOTATION_TYPE.ELLIPSE:
      // @ts-ignore The case check confirms that from and toData will be correct type.
      return interpolateEllipse(fromData, toData, interpolationFactor);
    case ANNOTATION_TYPE.POLYLINE:
    case ANNOTATION_TYPE.POLYGON:
      // Polyline and polygon follow the same rule
      // @ts-ignore The case check confirms that from and toData will be correct type.
      return interpolatePolyline(fromData, toData, interpolationFactor);
    case ANNOTATION_TYPE.CUBOID:
      // @ts-ignore The case check confirms that from and toData will be correct type.
      return interpolateCuboid(fromData, toData, interpolationFactor);
    case ANNOTATION_TYPE.SEGMENTATION:
      // @ts-ignore The case check confirms that from and toData will be correct type.
      return fromData;
    case ANNOTATION_TYPE.SKELETON:
      // @ts-ignore The case check confirms that from and toData will be correct type.
      return interpolateSkeleton(fromData, toData, interpolationFactor);
    case ANNOTATION_TYPE.FRAME_CLASSIFICATION:
      return fromData;
  }
}